'use client';

import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  ScanLine
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { photoScanningQueries } from '../services/photo-scanning.queries';
import type { ScanBatch, BatchPageRecord } from '../types';

// ─── Counter card ────────────────────────────────────────────────────────────
interface CounterCardProps {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  icon: React.ReactNode;
}

function CounterCard({
  label,
  value,
  colorClass,
  bgClass,
  icon
}: CounterCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border p-4',
        bgClass
      )}
    >
      <div className={cn('mb-1 text-3xl font-bold', colorClass)}>{value}</div>
      <div className='text-muted-foreground flex items-center gap-1 text-xs'>
        {icon}
        {label}
      </div>
    </div>
  );
}

// ─── Monitoring tab ──────────────────────────────────────────────────────────
export function MonitoringTab({ batchId }: { batchId: number }) {
  const t = useTranslations('photoScanning');
  const { data: rawBatch } = useQuery(photoScanningQueries.detail(batchId));
  const batch = rawBatch?.data as
    | (ScanBatch & { pages: BatchPageRecord[] })
    | undefined;

  const pages: BatchPageRecord[] = batch?.pages ?? [];
  const actualPages = pages.filter((p) => p.item_type === 'page');
  const qrSuccess = actualPages.filter((p) => p.qr_status === 'success').length;
  const qrFailed = actualPages.filter((p) => p.qr_status === 'failed').length;
  const fragile = actualPages.filter((p) => p.status === 'fragile').length;

  return (
    <div className='space-y-5'>
      <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
        <CounterCard
          label={t('monitoringTotalScanned')}
          value={actualPages.length}
          colorClass='text-blue-600'
          bgClass='bg-blue-50 dark:bg-blue-950/30'
          icon={<ScanLine className='h-3 w-3' />}
        />
        <CounterCard
          label={t('monitoringQrSuccess')}
          value={qrSuccess}
          colorClass='text-emerald-600'
          bgClass='bg-emerald-50 dark:bg-emerald-950/30'
          icon={<CheckCircle2 className='h-3 w-3' />}
        />
        <CounterCard
          label={t('monitoringQrFailed')}
          value={qrFailed}
          colorClass='text-rose-600'
          bgClass='bg-rose-50 dark:bg-rose-950/30'
          icon={<XCircle className='h-3 w-3' />}
        />
        <CounterCard
          label={t('monitoringFragile')}
          value={fragile}
          colorClass='text-amber-600'
          bgClass='bg-amber-50 dark:bg-amber-950/30'
          icon={<AlertTriangle className='h-3 w-3' />}
        />
      </div>

      <div className='flex items-center justify-between'>
        <p className='text-sm font-semibold'>{t('activityLog')}</p>
        <div className='flex gap-2'>
          <Button variant='outline' size='sm'>
            {t('filterLabel')}
          </Button>
          <Button variant='outline' size='sm' className='gap-1.5'>
            <FileText className='h-3.5 w-3.5' />
            {t('exportCsv')}
          </Button>
        </div>
      </div>

      <div className='rounded-md border'>
        <table className='w-full text-xs'>
          <thead>
            <tr className='bg-muted/50 text-muted-foreground border-b'>
              <th className='px-3 py-2 text-start'>{t('tableSequence')}</th>
              <th className='px-3 py-2'>{t('tablePageId')}</th>
              <th className='px-3 py-2'>{t('tableItemType')}</th>
              <th className='px-3 py-2'>{t('tableQrResult')}</th>
              <th className='px-3 py-2'>{t('tableDpi')}</th>
              <th className='px-3 py-2'>{t('tableTime')}</th>
              <th className='px-3 py-2 text-start'>{t('tableFileRef')}</th>
              <th className='px-3 py-2 text-end'>{t('tableStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className='text-muted-foreground px-3 py-8 text-center'
                >
                  —
                </td>
              </tr>
            ) : (
              pages.map((page) => (
                <tr
                  key={page.id}
                  className='hover:bg-muted/30 border-b last:border-0'
                >
                  <td className='px-3 py-2.5 font-mono'>#{page.sequence}</td>
                  <td className='px-3 py-2.5 text-center font-mono'>
                    {page.page_number}
                  </td>
                  <td className='px-3 py-2.5 text-center'>
                    <Badge
                      variant='outline'
                      className={cn(
                        'text-xs',
                        page.item_type === 'box'
                          ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : page.item_type === 'container'
                            ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-300'
                      )}
                    >
                      {page.item_type === 'box'
                        ? t('itemTypeBox')
                        : page.item_type === 'container'
                          ? t('itemTypeContainer')
                          : t('itemTypePage')}
                    </Badge>
                  </td>
                  <td className='px-3 py-2.5 text-center'>
                    {page.qr_status === 'success' ? (
                      <CheckCircle2 className='mx-auto h-4 w-4 text-emerald-500' />
                    ) : (
                      <XCircle className='mx-auto h-4 w-4 text-rose-500' />
                    )}
                  </td>
                  <td className='px-3 py-2.5 text-center'>DPI {page.dpi}</td>
                  <td className='px-3 py-2.5 text-center'>
                    {page.scan_time_ms}ms
                  </td>
                  <td className='text-muted-foreground px-3 py-2.5 font-mono'>
                    ...{page.file_ref.slice(-20)}
                  </td>
                  <td className='px-3 py-2.5 text-end'>
                    <Badge
                      variant={
                        page.status === 'success'
                          ? 'default'
                          : page.status === 'failed'
                            ? 'destructive'
                            : 'outline'
                      }
                      className='text-xs'
                    >
                      {page.status === 'success'
                        ? t('statusSuccess')
                        : page.status === 'failed'
                          ? t('statusFailed')
                          : t('statusFragile')}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
